// Karuzela subelementów produktu (Podobne / Akcesoria / ...) - odpowiednik WiseHomeSubelements,
// ale używa hooków produktu (`useGetProductSubelementsTypes` / `useGetProductSubelementItems`).
// Layout 1:1 z `WiseHomeSubelements`: WiseCarousel z tabs + Slider z `WiseProductItem`.

import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import classnames from 'classnames';
import { Slide, Slider } from 'pure-react-carousel';

import { useRWD } from 'hooks';
import {
  useGetProductSubelementsTypes,
  useGetProductSubelementItems
} from 'api';
import { Container } from 'components/controls';
import { WiseProductItem } from 'components/containers';
import WiseCarousel, { type WiseCarouselTab } from 'pages/wise/WiseProduct/components/WiseCarousel';

import styles from 'theme/components/wise/WiseHomeSubelements/WiseHomeSubelements.module.scss';

const ITEMS_PER_PAGE = 999;
const CARD_W = 260;
const CARD_H = 465;
const MAX_VISIBLE_SLIDES = 5;
const CARD_GAP = 8;

function cardsPerRow(widthPx: number): number {
  if (widthPx <= 0) return 1;
  const slot = CARD_W + CARD_GAP;
  return Math.max(1, Math.floor((widthPx + CARD_GAP) / slot));
}

export type WiseProductSubelementsProps = {
  productId: number;
};

const WiseProductSubelements: React.FC<WiseProductSubelementsProps> = ({ productId }) => {
  const { isMobile } = useRWD();
  const [activeTypeId, setActiveTypeId] = useState<number | null>(null);
  const blockRef = useRef<HTMLDivElement>(null);
  const [fitsInRow, setFitsInRow] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const { data: typesData } = useGetProductSubelementsTypes(
    productId,
    { page: 1, limit: 999 },
    { enabled: !!productId }
  );

  useEffect(() => {
    const first = typesData?.items?.[0];
    if (first == null || activeTypeId != null) return;
    setActiveTypeId(first.id);
  }, [typesData, activeTypeId]);

  const { data: itemsData, isFetching } = useGetProductSubelementItems(
    productId,
    activeTypeId ?? 0,
    { page: 1, limit: ITEMS_PER_PAGE },
    { enabled: !!productId && !!activeTypeId }
  );

  const products = itemsData?.items ?? [];
  const count = products.length;

  useLayoutEffect(() => {
    const el = blockRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const measure = () => {
      const w = el.getBoundingClientRect().width;
      setContainerWidth(w);
      setFitsInRow(cardsPerRow(w));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [count]);

  const visibleSlides = useMemo(() => {
    if (count < 1) return 1;
    if (isMobile) {
      // Powyżej 650px (np. tablet portrait) pokazujemy 2 karty zamiast 1.
      return Math.min(count, containerWidth >= 650 ? 2 : 1);
    }
    return Math.min(MAX_VISIBLE_SLIDES, count, Math.max(1, fitsInRow ?? MAX_VISIBLE_SLIDES));
  }, [count, isMobile, fitsInRow, containerWidth]);

  const tabs: WiseCarouselTab[] = useMemo(
    () => typesData?.items.map((t) => ({ id: String(t.id), label: t.name })) ?? [],
    [typesData]
  );

  const handleTabChange = (tabId: string) => {
    const match = typesData?.items?.find((t) => String(t.id) === tabId);
    if (match) setActiveTypeId(match.id);
  };

  if (!typesData?.items?.length || activeTypeId == null) return null;

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Pages-Wise-WiseProduct-components-WiseProductSubelements'
      )}>
      <Container>
        <div ref={blockRef} className={styles.sliderBlock}>
          <WiseCarousel
            className={styles.carousel}
            tabs={tabs}
            activeTabId={String(activeTypeId)}
            onTabChange={handleTabChange}
            totalSlides={count}
            visibleSlides={visibleSlides}
            naturalSlideWidth={CARD_W}
            naturalSlideHeight={CARD_H}
            relaxHeight
            showNavArrows={!isMobile && count > 0}
            disableKeyboard
            disableAnimation={isFetching}
            isLoading={isFetching && count === 0}>
            {count > 0 ? (
              <Slider>
                {products.map((product, index) => (
                  <Slide key={product.id} index={index} className={styles.carouselSlide}>
                    <div className={styles.productWrapper}>
                      <WiseProductItem product={product} />
                    </div>
                  </Slide>
                ))}
              </Slider>
            ) : null}
          </WiseCarousel>
        </div>
      </Container>
    </div>
  );
};

export default WiseProductSubelements;
